// SmsCodeInput.tsx
import { useRef, useState } from 'react';
import bemClassName from '../../utils/bem';
import './index.scss';

interface ISmsCodeInput {
  length?: number;
  onComplete: (code: string) => void;
  isValid?: boolean;
  externalClassName?: string;
  errorText?: string;
  disabled?: boolean;
}

const smsCodeInput = bemClassName('sms-code-input');

const SmsCodeInput: React.FC<ISmsCodeInput> = ({
  length = 4,
  onComplete,
  isValid = true,
  externalClassName = '',
  errorText = 'Код должен состоять из 4 цифр', // Значение по умолчанию
  disabled = false,
}) => {
  const [code, setCode] = useState<string[]>(Array(length).fill(''));
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  const handleInputChange = (value: string, index: number) => {
    if (disabled) return;
    if (!/^\d*$/.test(value)) return;

    const newCode = [...code];
    newCode[index] = value;
    setCode(newCode);

    if (value && index < length - 1) {
      inputRefs.current[index + 1]?.focus();
    }

    if (newCode.every((digit) => digit !== '')) {
      onComplete(newCode.join(''));
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, index: number) => {
    if (disabled) return;
    const { key } = e;

    if (key === 'Backspace') {
      e.preventDefault();
      const newCode = [...code];

      if (newCode[index]) {
        newCode[index] = '';
      } else if (index > 0) {
        newCode[index - 1] = '';
        inputRefs.current[index - 1]?.focus();
      }

      setCode(newCode);
    } else if (key === 'ArrowLeft' && index > 0) {
      inputRefs.current[index - 1]?.focus();
    } else if (key === 'ArrowRight' && index < length - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    if (disabled) return;
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').split('');

    if (pastedData.length === length) {
      setCode(pastedData);
      inputRefs.current[length - 1]?.focus();
      onComplete(pastedData.join(''));
    } else if (pastedData.length > 0) {
      const newCode = [...code];
      pastedData.forEach((digit, i) => {
        if (i < length) {
          newCode[i] = digit;
        }
      });
      setCode(newCode);

      if (newCode.every((digit) => digit !== '')) {
        onComplete(newCode.join(''));
      }
    }
  };

  return (
    <div className={`${smsCodeInput()} ${externalClassName}`}>
      <p className={smsCodeInput('label')}>Код подтверждения</p>
      <div
        className={smsCodeInput('inputs', {
          error: !isValid && code.every((digit) => digit !== ''),
        })}>
        {Array.from({ length }).map((_, index) => (
          <input
            key={index}
            ref={(el) => {
              inputRefs.current[index] = el;
            }}
            type="text"
            inputMode="numeric"
            maxLength={1}
            value={code[index]}
            onChange={(e) => handleInputChange(e.target.value, index)}
            onKeyDown={(e) => handleKeyDown(e, index)}
            onPaste={handlePaste}
            disabled={disabled}
            className={smsCodeInput('input')}
            aria-label={`Код цифра ${index + 1}`}
          />
        ))}
      </div>
      {!isValid && <p className={smsCodeInput('error')}>{errorText}</p>}
    </div>
  );
};

export default SmsCodeInput;
